import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  ClickAwayListener,
  InputBase,
  Paper,
  Typography,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { useNavigate } from 'react-router-dom';
import { useHeaderSearch } from './useHeaderSearch';
import { SearchResultItem, EntityKind } from './types';

const KIND_LABELS: Record<string, string> = {
  component: 'SERVICES & COMPONENTS',
  template: 'TEMPLATES',
  api: 'APIS',
  group: 'TEAMS & GROUPS',
};

const KIND_ORDER: EntityKind[] = ['component', 'template', 'api', 'group'];

const KIND_COLORS: Record<string, string> = {
  component: '#1d9e75',
  template: '#7f77dd',
  api: '#d4a017',
  group: '#d85a30',
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    flex: 1,
    maxWidth: 420,
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,0,0,0.04)',
    borderRadius: 8,
    padding: '4px 12px',
    gap: 8,
  },
  inputWrapperFocused: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.06)',
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  searchIcon: {
    fontSize: 20,
    opacity: 0.5,
  },
  input: {
    flex: 1,
    fontSize: 13,
  },
  shortcutHint: {
    fontSize: 11,
    opacity: 0.35,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '1px 6px',
    fontFamily: 'monospace',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[4],
    zIndex: theme.zIndex.modal,
    maxHeight: 420,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
  },
  groupHeader: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    opacity: 0.5,
    padding: '12px 16px 4px',
    textTransform: 'uppercase' as const,
  },
  resultItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '8px 16px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(0,0,0,0.02)',
    },
  },
  resultItemHighlighted: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,0,0,0.04)',
  },
  kindChip: {
    height: 20,
    fontSize: 10,
    fontWeight: 600,
  },
  resultName: {
    fontSize: 13,
    fontWeight: 500,
  },
  resultDesc: {
    fontSize: 11,
    opacity: 0.5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  highlight: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,0,0.2)'
        : 'rgba(255,200,0,0.3)',
    borderRadius: 2,
  },
  footer: {
    padding: '10px 16px',
    borderTop: `1px solid ${theme.palette.divider}`,
    textAlign: 'center' as const,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(0,0,0,0.02)',
    },
  },
  footerText: {
    fontSize: 12,
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
}));

function HighlightedText({
  text,
  query,
  className,
}: {
  text: string;
  query: string;
  className: string;
}) {
  if (!query) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'i'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className={className}>
            {part}
          </span>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

export function SearchBar() {
  const classes = useStyles();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const { results, totalCount, loading } = useHeaderSearch(query);

  const showDropdown =
    focused && query.length > 0 && (results.length > 0 || loading);

  // Group results by kind
  const grouped = KIND_ORDER.reduce<Record<string, SearchResultItem[]>>(
    (acc, kind) => {
      const items = results.filter(r => r.kind === kind);
      if (items.length > 0) acc[kind] = items;
      return acc;
    },
    {},
  );

  const flatResults = KIND_ORDER.flatMap(kind => grouped[kind] || []);

  // Global `/` shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (e.target as HTMLElement)?.isContentEditable
      )
        return;
      if (e.key === '/') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setQuery('');
      inputRef.current?.blur();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, -1));
    } else if (
      e.key === 'Enter' &&
      highlightIndex >= 0 &&
      flatResults[highlightIndex]
    ) {
      navigate(flatResults[highlightIndex].link);
      setQuery('');
      inputRef.current?.blur();
    }
  };

  const handleClose = () => {
    setFocused(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box className={classes.root}>
        <Box
          className={`${classes.inputWrapper} ${focused ? classes.inputWrapperFocused : ''}`}
        >
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            inputRef={inputRef}
            className={classes.input}
            placeholder="Search catalog, templates, APIs…"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setHighlightIndex(-1);
            }}
            onFocus={() => setFocused(true)}
            onKeyDown={handleKeyDown}
          />
          {!focused && <span className={classes.shortcutHint}>/</span>}
        </Box>
        {showDropdown && (
          <Paper className={classes.dropdown} elevation={0}>
            {Object.entries(grouped).map(([kind, items]) => (
              <Box key={kind}>
                <Typography className={classes.groupHeader}>
                  {KIND_LABELS[kind] || kind}
                </Typography>
                {items.map(item => {
                  const idx = flatResults.indexOf(item);
                  return (
                    <Box
                      key={`${item.kind}-${item.namespace}-${item.name}`}
                      className={`${classes.resultItem} ${idx === highlightIndex ? classes.resultItemHighlighted : ''}`}
                      onClick={() => {
                        navigate(item.link);
                        setQuery('');
                        setFocused(false);
                      }}
                    >
                      <Chip
                        label={item.kind}
                        size="small"
                        className={classes.kindChip}
                        style={{
                          backgroundColor: KIND_COLORS[item.kind] || '#888',
                          color: '#fff',
                        }}
                      />
                      <Box flex={1} style={{ minWidth: 0 }}>
                        <Typography className={classes.resultName}>
                          <HighlightedText
                            text={item.name}
                            query={query}
                            className={classes.highlight}
                          />
                        </Typography>
                        {item.description && (
                          <Typography className={classes.resultDesc}>
                            {item.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
            {totalCount > 8 && (
              <Box
                className={classes.footer}
                onClick={() => {
                  navigate(`/search?query=${encodeURIComponent(query)}`);
                  setQuery('');
                  setFocused(false);
                }}
              >
                <Typography className={classes.footerText}>
                  View all {totalCount} results for &ldquo;{query}&rdquo;
                  &rarr;
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}
